import React, { ReactNode } from 'react';

import PropTypes from 'prop-types';

import { useDeprecate } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { useTheme, useComponentIcons } from '@adsk/alloy-react-styles';
import type { NotificationToastDesignTokens } from '@adsk/alloy-react-styles';
import {
  stylePropType,
  StylableComponent,
  StyleProp,
} from '@adsk/alloy-react-theme';

import { NOTIFICATION_STATUSES, NotificationStatus } from './helpers/consts';
import useAsyncNotification from './helpers/useAsyncNotification';
import NotificationCloseButton from './NotificationCloseButton';
import NotificationIcon from './NotificationIcon';

type NotificationProps = StylableComponent<HTMLDivElement> & {
  footer?: ReactNode;
  onDismiss?: () => void;
  /**
   * @deprecated
   * autoDismiss should be applied to useNotifications().showNotification
   */
  autoDismiss?: number;
  dismissible?: boolean;
  showIcon?: boolean;
  status?: NotificationStatus;
  asyncOperation?: Promise<unknown>;
};

const rootBaseStyle: StyleProp = {
  display: 'flex',
  flexDirection: 'column',
  maxWidth: 416,
  minHeight: 48,
  borderLeftStyle: 'solid',
  boxSizing: 'border-box',
  animation: 'alloy-notification-fade-in 0.2s ease-out',
  '@keyframes alloy-notification-fade-in': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
};

const Notification = React.forwardRef<HTMLDivElement, NotificationProps>(
  (
    {
      style,
      className,
      status = NOTIFICATION_STATUSES.INFO,
      showIcon = true,
      children,
      footer,
      dismissible = true,
      onDismiss,
      asyncOperation,
      autoDismiss,
      ...props
    },
    ref,
  ) => {
    useDeprecate(
      autoDismiss !== undefined,
      'Notification',
      'autoDismiss',
      'useNotifications().showNotification autoDismiss argument',
    );

    const themeTokens = useTheme();
    const { closeIcon: CloseIcon } = useComponentIcons('notification');
    const notifTokens = (themeTokens.tokens?.component?.notificationToast ??
      themeTokens.tokens?.component?.notificationtoast) as
      | NotificationToastDesignTokens
      | undefined;
    const contentContainerTokens = notifTokens?.['content-container'];

    const { isLoading, finalStatus, asyncDismissible, activeFooter, message } =
      useAsyncNotification({ asyncOperation, status, footer });
    const finalShowIcon = showIcon && status !== NOTIFICATION_STATUSES.NONE;

    const statusTokens = notifTokens?.[finalStatus];
    const iconContainerTokens = notifTokens?.['icon-container'];
    const iconGapTokens = notifTokens?.['icon-gap'];
    const closeButtonTokens = notifTokens?.['close-button'];
    const messageContainerTokens = notifTokens?.['message-container'];

    const isWeaveMode = !!contentContainerTokens;

    const iconWrapperStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: iconContainerTokens?.['padding-left'],
      paddingRight: iconContainerTokens?.['padding-right'],
      marginRight: iconGapTokens?.['margin-right'],
    };

    const renderLeading = (node: React.ReactElement<{ style?: StyleProp }>) =>
      isWeaveMode ? (
        <div style={iconWrapperStyle}>{node}</div>
      ) : (
        React.cloneElement(node, {
          style: { marginRight: iconGapTokens?.['margin-right'] },
        })
      );

    return (
      <div
        {...props}
        className={className}
        ref={ref}
        role="alert"
        css={[
          rootBaseStyle,
          {
            minWidth: notifTokens?.width,
            backgroundColor:
              statusTokens?.['background-color'] ??
              notifTokens?.['background-color'],
            borderRadius: notifTokens?.['border-radius'],
            borderLeftWidth: notifTokens?.['border-left-width'],
            borderLeftColor: statusTokens?.['border-left-color'],
            boxShadow: notifTokens?.['box-shadow'],
            paddingTop: notifTokens?.['padding-top'],
            paddingRight: notifTokens?.['padding-right'],
            paddingBottom: notifTokens?.['padding-bottom'],
            paddingLeft: notifTokens?.['padding-left'],
          },
          style,
        ]}
      >
        <div
          style={{
            display: 'flex',
            alignItems: isWeaveMode ? 'center' : undefined,
            paddingTop: contentContainerTokens?.['padding-top'],
            paddingRight: contentContainerTokens?.['padding-right'],
            paddingBottom: contentContainerTokens?.['padding-bottom'],
            paddingLeft: contentContainerTokens?.['padding-left'],
          }}
        >
          {finalShowIcon &&
            !isLoading &&
            renderLeading(<NotificationIcon status={finalStatus} />)}
          {isLoading &&
            renderLeading(<ProgressRing size={ProgressRing.SIZES.SMALL} />)}
          <div
            dir="auto"
            style={{
              flex: 1,
              wordBreak: 'break-word',
              marginRight: contentContainerTokens?.['margin-right'],
              paddingTop: messageContainerTokens?.['padding-top'],
              paddingBottom: messageContainerTokens?.['padding-bottom'],
              fontSize: messageContainerTokens?.['font-size'],
              fontWeight: messageContainerTokens?.['font-weight'],
              lineHeight: messageContainerTokens?.['line-height'],
              color: messageContainerTokens?.['text-color'],
            }}
          >
            {message ?? children}
          </div>
          {(dismissible !== false || asyncDismissible) && (
            <NotificationCloseButton
              tokens={closeButtonTokens}
              onClick={onDismiss}
            >
              <CloseIcon />
            </NotificationCloseButton>
          )}
        </div>
        {activeFooter !== null &&
          activeFooter !== undefined &&
          (isWeaveMode ? (
            <div
              style={{
                paddingLeft: iconContainerTokens?.['padding-left'],
                paddingRight: iconContainerTokens?.['padding-right'],
                paddingBottom: contentContainerTokens?.['padding-bottom'],
              }}
            >
              {activeFooter}
            </div>
          ) : (
            activeFooter
          ))}
      </div>
    );
  },
);

Notification.displayName = 'Notifications';

Notification.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Notification's content */
  children: PropTypes.any,
  /** Notification's footer */
  footer: PropTypes.any,
  /** Called on dismiss */
  onDismiss: PropTypes.func,
  /** Deprecated: Controls whether the notification should automatically dismiss */
  autoDismiss: PropTypes.number,
  /** Controls whether the notification is dismissible */
  dismissible: PropTypes.bool,
  /** Toggle icon visibility */
  showIcon: PropTypes.bool,
  /** Notification status */
  status: PropTypes.oneOf(Object.values(NOTIFICATION_STATUSES)),
  /** A Promise if the toast needs to be changed after rendered.
   When the promise resolves it should return an object with `message` and `status` to update the toast state. */
  asyncOperation: PropTypes.instanceOf(Promise),
};

export default Notification;
